import { apiGet, AppModels, AppState, nfcTagByBarCode, ProductAttrs, productByBarCode, productSlice, ScanViewModeSlice, SearchProductResult, ToolAttrs, toolByBarCode } from 'cdx-common';
import React, { createContext, useContext } from 'react';
import { useSelector } from 'react-redux';
import { handleServerResult } from '../../libraries/MainViewLoader';
import { appStore } from '../../store';

declare const process: { env: { mode?: string; }; };

export interface ScanWatcherInterface {
    onUPCCodeScanned: (func: (result: SearchProductResult) => void) => void,
    offUPCCodeScanned: (func: (result: SearchProductResult) => void) => void,
    onLocalBarCodeScanned: (func: (code: string) => void) => void,
    offLocalBarCodeScanned: (func: (code: string) => void) => void,
    onProductScanned: (func: (product: ProductAttrs) => void) => void,
    onToolScanned: (func: (tool: ToolAttrs) => void) => void,
    onRelatedElementScanned: (func: (type: AppModels, id: ModelId) => void) => void,
    offProductScanned: (func: (product: ProductAttrs) => void) => void,
    offToolScanned: (func: (tool: ToolAttrs) => void) => void,
    offRelatedElementScanned: (func: (type: AppModels, id: ModelId) => void) => void,
}
const ScanWatcherInterfaceContext = createContext({} as unknown as ScanWatcherInterface);

interface KeyPressMonitor {
    key: string,
    ms: number,
    delayFromPrevious: number;
}
// ce n'est pas géré à même le component car les événements surviennent trop rapidement. Le DOM ne suit pas.
const monitor: KeyPressMonitor[] = [];
const keyPressDelay = process.env.mode === 'development' ? 500 : 200;

export const ScanWatcher = (props: { children?: React.ReactNode; }) => {

    const [textfieldIsCurrentlyFocused, setTextfieldIsCurrentlyFocused] = React.useState(false);
    const [keytimer, setKeytimer] = React.useState<number | null>(null);

    const [productScanListeners, setProductScanListeners] = React.useState<((product: ProductAttrs) => void)[]>([]);
    const [toolScanListeners, setToolScanListeners] = React.useState<((tool: ToolAttrs) => void)[]>([]);
    const [relatedElementScanListeners, setRealtedElementScanListeners] = React.useState<((type: AppModels, id: ModelId) => void)[]>([]);
    const [upcScanListeners, setUpcScanListeners] = React.useState<((result: SearchProductResult) => void)[]>([]);
    const [localBarCodeListeners, setLocalBarCodeListeners] = React.useState<((code: string) => void)[]>([]);

    const [lookingUp, setIsLookingUp] = React.useState<string | undefined>();

    const codeScanned = useSelector((state: AppState) => state.scanViewMode.codeScannded);
    const scannedTool = useSelector((state: AppState) => codeScanned ? toolByBarCode(state, codeScanned) : null);
    const scannedProduct = useSelector((state: AppState) => productByBarCode(state, codeScanned));
    const scanneNfcTag = useSelector((state: AppState) => nfcTagByBarCode(state, codeScanned));
    //const scannedWarehouse = useSelector((state: AppState) => warehouseByNfcTag(state, scanneNfcTag));



    React.useEffect(() => {
        console.log('effect in scanwatcher', codeScanned, scannedProduct, scannedTool, scanneNfcTag);
        if (!codeScanned) {
            return;
        }

        if (scannedProduct) {
            productScanListeners.forEach(listener => listener(scannedProduct));
            initialize();
        }

        if (scannedTool) {
            toolScanListeners.forEach(listener => listener(scannedTool));
            initialize();
        }


        if (scanneNfcTag && !scannedProduct && !scannedTool) {
            const t = scanneNfcTag.relatedType;
            const i = scanneNfcTag.relatedId;
            if (t && i) {
                relatedElementScanListeners.forEach(listener => listener(t, i));
                initialize();
            }
        }


        if (!scannedProduct && !scannedTool && !scanneNfcTag) {
            if (codeScanned) {
                if (codeScanned != lookingUp) {
                    setIsLookingUp(codeScanned);
                    // vérifier si c'est un barcode loval (T00000213) ou un code upc autrement
                    if (codeScanned.toLocaleLowerCase().startsWith('t')) {
                        searchLocalBarCode();
                    } else {
                        searchUpc();
                    }
                }
            }
        }
    }, [codeScanned, scannedProduct, scannedTool, scanneNfcTag, productScanListeners, toolScanListeners, relatedElementScanListeners, upcScanListeners, localBarCodeListeners]);

    const initialize = () => {
        appStore.dispatch(ScanViewModeSlice.actions.setCodeScanned(''));
    };

    const searchLocalBarCode = async () => {
        if (codeScanned) {
            try {
                const response = await apiGet('searchLocalBarcode', { barcode: codeScanned }, undefined);

                if (response) {

                    handleServerResult(response);
                    localBarCodeListeners.forEach(listener => listener(codeScanned));
                    setIsLookingUp(undefined);
                } else {

                    localBarCodeListeners.forEach(listener => listener(codeScanned));
                    initialize();
                }
            } catch (err) {
                setIsLookingUp(undefined);
            }
        }
    };
    const searchUpc = async () => {
        if (codeScanned) {
            try {
                const previousCodeScanned = codeScanned;
                const response = await apiGet('productSearchByUpc', { upc: codeScanned }, undefined);
                if (response) {
                    if ('id' in response) {
                        appStore.dispatch(productSlice.actions.upsertOne(response));
                    } else if (Array.isArray(response) && response[0].length > 0) {
                        // faire le call manuellement ici.
                        if (previousCodeScanned === codeScanned) {
                            upcScanListeners.forEach(listener => listener(response));
                            initialize();
                        }
                    }
                    setIsLookingUp(undefined);
                }
            } catch (err) {
                setIsLookingUp(undefined);
            }
        }
    };


    const onKeyUp = (e: KeyboardEvent) => {
        if (!textfieldIsCurrentlyFocused) {
            const now = new Date().getTime();
            const last = monitor.length > 0 ? monitor[monitor.length - 1] : undefined;
            const delayFromPrevious = last ? now - last.ms : 0;
            monitor.push({ key: e.key, ms: new Date().getTime(), delayFromPrevious });
            if (keytimer) { clearTimeout(keytimer); }
            setKeytimer(setTimeout(onTimeout, keyPressDelay));
        }
    };

    const onTimeout = () => {

        // Si deux touches sont trop éloignées, la séquence ne provient pas du lecteur de code-barres.

        if (monitor.find(p => p.delayFromPrevious > keyPressDelay)) {
            // on flush
        } else {
            // S'il y a au moins 5 caractères alphanumériques, c'est une lecture de code-barres.
            const enterKey = monitor.findIndex(p => p.key.toLowerCase() == 'enter');

            const normalizedMonitor = monitor.splice(0, enterKey);

            const code = normalizedMonitor
                .map(p => p.key)
                .filter(key => /^[a-zA-Z0-9]$/.test(key))
                .join('');

            const normalized = code.toLocaleLowerCase().startsWith('t') ? code.toLowerCase() : code;

            if (normalized.length >= 5) {
                appStore.dispatch(ScanViewModeSlice.actions.setCodeScanned(normalized));
            }
        }
        monitor.splice(0, monitor.length);
        setKeytimer(null);
    };

    React.useEffect(() => {
        const handleKeyUp = (e: KeyboardEvent) => {
            onKeyUp(e);
        };

        const handleFocusIn = (e: FocusEvent) => {
            const target = e.target as HTMLElement;
            if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
                setTextfieldIsCurrentlyFocused(true);
            }
        };

        const handleFocusOut = (e: FocusEvent) => {
            const target = e.target as HTMLElement;
            if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
                // Check if the new active element is not an input or textarea
                const activeElement = document.activeElement;
                if (!(activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA'))) {
                    setTextfieldIsCurrentlyFocused(false);
                }
            }
        };

        document.addEventListener('focusin', handleFocusIn);
        document.addEventListener('focusout', handleFocusOut);
        document.addEventListener('keyup', handleKeyUp);

        return () => {

            document.removeEventListener('focusin', handleFocusIn);
            document.removeEventListener('focusout', handleFocusOut);
            document.removeEventListener('keyup', handleKeyUp);
        };
    }, []);

    const addOnProductScanned = (func: (model: ProductAttrs) => void) => {
        setProductScanListeners(p => [...p, func]);
    };
    const removeOnProductScanned = (func: (model: ProductAttrs) => void) => {
        setProductScanListeners(p => p.filter(q => q !== func));
    };
    const addOnToolScanned = (func: (model: ToolAttrs) => void) => {
        setToolScanListeners(p => [...p, func]);
    };
    const removeOnToolScanned = (func: (model: ToolAttrs) => void) => {
        setToolScanListeners(p => p.filter(q => q !== func));
    };
    const addOnRelatedElementScanned = (func: (type: AppModels, id: ModelId) => void) => {
        setRealtedElementScanListeners(p => [...p, func]);
    };

    const removeOnRelatedElementScanned = (func: (type: AppModels, id: ModelId) => void) => {
        setRealtedElementScanListeners(p => p.filter(q => q !== func));
    };

    const onUPCCodeScanned = (func: (result: SearchProductResult) => void) => {
        setUpcScanListeners(p => [...p, func]);
    };

    const removeUPCCodeScanned = (func: (result: SearchProductResult) => void) => {
        setUpcScanListeners(p => p.filter(q => q !== func));
    };
    const addOnLocalBarCodeScanned = (func: (code: string) => void) => {
        setLocalBarCodeListeners(p => [...p, func]);
    };
    const removeOnLocalBarCodeScanned = (func: (code: string) => void) => {
        setLocalBarCodeListeners(p => p.filter(q => q !== func));
    };

    return <ScanWatcherInterfaceContext.Provider value={{
        onProductScanned: addOnProductScanned,
        onToolScanned: addOnToolScanned,
        onRelatedElementScanned: addOnRelatedElementScanned,
        offProductScanned: removeOnProductScanned,
        offToolScanned: removeOnToolScanned,
        offRelatedElementScanned: removeOnRelatedElementScanned,
        onUPCCodeScanned: onUPCCodeScanned,
        offUPCCodeScanned: removeUPCCodeScanned,
        onLocalBarCodeScanned: addOnLocalBarCodeScanned,
        offLocalBarCodeScanned: removeOnLocalBarCodeScanned
    }}>{props.children}</ScanWatcherInterfaceContext.Provider>;
};

export const useScanWatcher = () => useContext(ScanWatcherInterfaceContext);
